/* GFI Nexus — HTML → PDF renderer
   ─────────────────────────────────────────────────────────────────
   The print templates (buildCartaPorteHTML, buildInvoicePrintHTML,
   the Receiving Manifest, the Loading Docs) generate full HTML
   documents intended for the browser's native Print dialog. For
   Drive uploads we need a PDF file — this helper rasterises the
   HTML offscreen with html2canvas and stuffs the image into a
   multi-page jsPDF document.

   Trade-off vs. native browser print: the output is an image-based
   PDF, not text-selectable. For internal-ops docs this is fine, and
   it avoids needing a server-side Chromium for headless print. */

import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

/* Render an HTML string into an offscreen iframe so the host page's
   styles can't leak in. Returns the iframe (caller is responsible
   for removing it) and the inner document. */
export function mountOffscreenIframe(html, widthPx) {
  const iframe = document.createElement("iframe");
  /* Position offscreen, fixed size matching Letter @ 96dpi (8.5in × ~). */
  iframe.style.position = "fixed";
  iframe.style.left = "-9999px";
  iframe.style.top = "0";
  iframe.style.width = widthPx + "px";
  iframe.style.height = "1200px"; /* gets re-sized to content height below */
  iframe.style.border = "0";
  document.body.appendChild(iframe);
  console.log("[htmlToPdfBlob] iframe appended");
  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  return iframe;
}

/* html2canvas is unreliable with SVG `<img>` tags whose source is a
   data: URL and whose intrinsic size comes only from a viewBox (no
   explicit width/height attrs). The captured bitmap ends up cropped,
   stretched, or blank. Workaround: BEFORE snapshotting, find every
   such img, rasterise the SVG into a high-DPI PNG canvas, and swap
   the src. Once it's a PNG data URI, html2canvas handles it fine.
   Called from htmlToPdfBlob after the iframe is laid out so
   offsetWidth/Height match the CSS-resolved display size. */
async function rasterizeSvgImages(doc) {
  const imgs = Array.from(doc.querySelectorAll("img"));
  for (const img of imgs) {
    const src = img.getAttribute("src") || "";
    /* Only data:image/svg+xml — http(s) SVGs and PNGs already work. */
    if (src.indexOf("data:image/svg") !== 0) continue;
    try {
      /* Make sure the original is loaded so offsetWidth/Height
         reflect the CSS-resolved size, not 0. */
      if (!img.complete) {
        await new Promise(function (resolve) {
          img.onload = resolve; img.onerror = resolve;
          /* Hard ceiling so a broken src doesn't hang. */
          setTimeout(resolve, 1500);
        });
      }
      const dispW = img.offsetWidth || img.naturalWidth || 200;
      const dispH = img.offsetHeight || img.naturalHeight || 100;
      /* 3× pixel ratio so the rasterised PNG stays sharp when
         html2canvas scales it back up at scale:3 later. */
      const k = 3;
      const targetW = Math.max(1, Math.round(dispW * k));
      const targetH = Math.max(1, Math.round(dispH * k));
      /* Draw the SVG into a fresh canvas — has to be a new Image
         because the original is rendered with viewBox auto-sizing
         that drawImage may also misread. We pass explicit dx/dy/dw/dh
         to drawImage to force the size. */
      const tmp = new Image();
      tmp.crossOrigin = "anonymous";
      await new Promise(function (resolve, reject) {
        tmp.onload = resolve;
        tmp.onerror = function () { reject(new Error("SVG image load failed")); };
        tmp.src = src;
      });
      const canvas = document.createElement("canvas");
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(tmp, 0, 0, targetW, targetH);
      img.src = canvas.toDataURL("image/png");
      /* Pin the displayed dimensions so the now-PNG img doesn't
         change layout from what the user saw at preview time. */
      if (dispW) img.style.width = dispW + "px";
      if (dispH) img.style.height = dispH + "px";
    } catch (e) {
      console.warn("[pdfRenderer] SVG rasterize failed for", src.substring(0, 60), e);
    }
  }
}

/* Wait for everything in the iframe to be ready: window load (fires
   when CSS + images finish), then document.fonts.ready (so html2canvas
   doesn't snap a screenshot mid-font-swap). Falls back to a small
   timeout if either signal never fires. */
export function waitForIframeReady(iframe) {
  return new Promise(function (resolve) {
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    function onReady() {
      console.log("[htmlToPdfBlob] iframe onload fired");
      const fontPromise = doc.fonts && doc.fonts.ready ? doc.fonts.ready : Promise.resolve();
      fontPromise.then(function () { console.log("[htmlToPdfBlob] fonts ready"); setTimeout(resolve, 50); }, function (e) { console.warn("[htmlToPdfBlob] fonts.ready rejected, continuing anyway", e); setTimeout(resolve, 50); });
    }
    if (doc.readyState === "complete") { onReady(); return; }
    iframe.onload = onReady;
    /* Hard ceiling so a missing asset doesn't hang the upload forever. */
    setTimeout(function () { console.warn("[htmlToPdfBlob] iframe ready 3.5s fallback fired"); onReady(); }, 3500);
  });
}

/* Shared canvas-rendering step used by both htmlToPdfBlob (single doc,
   may auto-paginate by height) and htmlDocsToPdfBlob (one doc per
   page, no height-slicing — each doc gets exactly one page).
   Mounts its own offscreen iframe and tears it down before
   returning/throwing — callers never see the iframe. */
async function renderHtmlDocToCanvas(html, widthPx, scale, logTag, minHeightPx) {
  const iframe = mountOffscreenIframe(html, widthPx);
  try {
    await waitForIframeReady(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    /* Strip default body margin/padding the print templates rely on
       the browser to drop in @page rules. html2canvas doesn't honor
       @page, so without this the captured bitmap is ~16 px taller
       than the visual content and the doc spills to page 2. */
    const resetStyle = doc.createElement("style");
    resetStyle.textContent = "html,body{margin:0!important;padding:0!important;background:#fff;}";
    doc.head && doc.head.appendChild(resetStyle);
    const body = doc.body;
    /* Pre-rasterise any SVG `<img>` (e.g. SweetLeaf logo) — html2canvas
       mishandles viewBox-only SVGs. Has to happen AFTER the iframe
       has laid out (so offsetWidth/Height are populated) but BEFORE
       the height re-measure so the swapped PNGs feed into the final
       contentHeight. */
    await rasterizeSvgImages(doc);
    /* Match iframe height to body content so html2canvas captures
       everything, not just the initial viewport. The 1200px floor
       (default) exists so an under-laid-out doc never captures a
       near-zero-height canvas — but it actively HURTS a genuinely
       short document (e.g. a compact report table with a handful of
       rows): forcing 1200px of "content" when the real table is only
       ~300px tall inflates the aspect-ratio math in htmlToPdfBlob
       enough to trigger its multi-page slicer, producing a blank
       trailing page (found 2026-09-17, Report Generator's PDF export
       — "literalmente no tiene bordes y agrega una hoja en blanco").
       Callers that know their content is legitimately short can pass
       a smaller `minHeightPx` to opt out of the floor; every existing
       caller that doesn't pass it keeps the original 1200 behavior
       unchanged. */
    const contentHeight = Math.max(body.scrollHeight, body.offsetHeight, minHeightPx != null ? minHeightPx : 1200);
    iframe.style.height = contentHeight + "px";
    console.log("[" + logTag + "] html2canvas start");
    const canvas = await html2canvas(body, {
      scale: scale,
      useCORS: true,
      backgroundColor: "#ffffff",
      windowWidth: widthPx,
      windowHeight: contentHeight,
    });
    console.log("[" + logTag + "] html2canvas done, canvas size:", canvas.width, canvas.height);
    return canvas;
  } finally {
    document.body.removeChild(iframe);
  }
}

/* Render the body of an HTML string to a PDF Blob.

   `opts`:
     - widthPx : iframe width (default 816 = 8.5in @ 96dpi, Letter)
     - format  : jsPDF format string (default "letter")
     - orientation : "portrait" | "landscape" (default "portrait")
     - scale   : html2canvas pixel ratio (default 3 for sharp text)
     - fitToleranceRatio : if content exceeds 1 page by less than this
       multiplier, shrink-to-fit instead of paginating (default 1.15).
       Stops single-page docs like the Carta Porte from spilling a
       sliver onto a blank page 2 just because the body picked up an
       extra 12 px of margin.
     - emptyPageThreshold : skip a subsequent page when the leftover
       content is less than this fraction of a page (default 0.04).
       Catches the "1057 px tall image on a 1056 px page" case where
       the second page is just a blank stripe.
     - marginPt : blank margin (points) left on all 4 sides of the
       page before the bitmap is placed (default 0 — every existing
       caller keeps its original edge-to-edge placement unchanged).
       Arturo, 2026-09-17: "los pdf aun los veo sin margen izquierdo y
       derecho, abarcan completamente la hoja de lado a lado" — the
       Report Generator's PDF sets this; nothing else needed to.

   Returns a Blob of MIME type application/pdf. */
export async function htmlToPdfBlob(html, opts) {
  opts = opts || {};
  const widthPx = opts.widthPx || 816;
  const format = opts.format || "letter";
  const orientation = opts.orientation || "portrait";
  const scale = opts.scale || 3;
  const fitToleranceRatio = opts.fitToleranceRatio || 1.15;
  const emptyPageThreshold = opts.emptyPageThreshold || 0.04;
  const minHeightPx = opts.minHeightPx;
  const marginPt = opts.marginPt || 0;

  try {
    const canvas = await renderHtmlDocToCanvas(html, widthPx, scale, "htmlToPdfBlob", minHeightPx);
    /* PNG instead of JPEG: avoids the JPEG compression artefacts on
       text edges. For text-heavy ops docs the file size is
       comparable thanks to PNG's deflate. */
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF({ unit: "pt", format: format, orientation: orientation, compress: true });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const availWidth = pageWidth - marginPt * 2;
    const availHeight = pageHeight - marginPt * 2;
    /* Width-fit: scale the bitmap to fill the available (margin-
       inset) width; height is whatever the aspect ratio yields. */
    const imgWidthFull = availWidth;
    const imgHeightFull = (canvas.height * imgWidthFull) / canvas.width;

    if (imgHeightFull <= availHeight * fitToleranceRatio) {
      /* Single-page mode. If it fits, drop it in unchanged; if it
         overflows by a sliver (<= fitToleranceRatio), shrink-to-fit
         and center horizontally so the doc still reads naturally
         (no stretched aspect ratio). */
      let finalW, finalH;
      if (imgHeightFull <= availHeight) {
        finalW = imgWidthFull;
        finalH = imgHeightFull;
      } else {
        const k = availHeight / imgHeightFull;
        finalW = imgWidthFull * k;
        finalH = availHeight;
      }
      const xOffset = marginPt + (availWidth - finalW) / 2;
      pdf.addImage(imgData, "PNG", xOffset, marginPt, finalW, finalH);
    } else {
      /* Multi-page slice. Each iteration repositions the same image
         up by one page's available height; jsPDF auto-clips to the
         page bounds. Skip adding a fresh page when the remainder is
         shorter than `emptyPageThreshold * availHeight` — that's the
         "blank second page" we kept hitting. */
      pdf.addImage(imgData, "PNG", marginPt, marginPt, imgWidthFull, imgHeightFull);
      let consumed = availHeight;
      while (imgHeightFull - consumed > availHeight * emptyPageThreshold) {
        pdf.addPage();
        pdf.addImage(imgData, "PNG", marginPt, marginPt - consumed, imgWidthFull, imgHeightFull);
        consumed += availHeight;
      }
    }
    const blob = pdf.output("blob");
    console.log("[htmlToPdfBlob] jsPDF blob created");
    return blob;
  } catch (e) {
    console.error("[htmlToPdfBlob] error:", e);
    throw e;
  }
}

/* Render MULTIPLE full HTML documents into ONE PDF, one page per doc,
   no height-based slicing — each doc is shrunk-to-fit onto exactly one
   page regardless of content length. Built for "N records = N pages"
   business rules (e.g. Sales Report: one bill per sheet) where
   htmlToPdfBlob's single-canvas height-slicing can't respect a
   document boundary because html2canvas doesn't understand CSS
   page-break properties at all — it just rasterises the whole DOM
   into one flat bitmap, so relying on `page-break-after` in the
   source HTML silently does nothing. Rendering each doc into its own
   offscreen iframe (rather than concatenating HTML and trying to
   locate the boundary inside one giant rasterised canvas) sidesteps
   that limitation entirely and also avoids any CSS bleed between
   docs. Each doc must be short enough to fit one page on its own
   (true for the Sales Report template, which is explicitly sized for
   1-page-per-bill) — there's no per-doc multi-page fallback here by
   design, matching "un bill = una hoja".

   `htmlDocs`: array of full HTML document strings (each with its own
   <style>, like buildPrintHTML's output). `opts` same shape as
   htmlToPdfBlob (widthPx/format/orientation/scale/fitToleranceRatio/
   marginPt — see htmlToPdfBlob's doc comment for marginPt, default 0
   so every existing caller keeps its exact edge-to-edge placement).
   Returns a Blob of MIME type application/pdf. */
export async function htmlDocsToPdfBlob(htmlDocs, opts) {
  opts = opts || {};
  if (!Array.isArray(htmlDocs) || htmlDocs.length === 0) return Promise.reject(new Error("htmlDocsToPdfBlob: no docs"));
  const widthPx = opts.widthPx || 816;
  const format = opts.format || "letter";
  const orientation = opts.orientation || "portrait";
  const scale = opts.scale || 3;
  const fitToleranceRatio = opts.fitToleranceRatio || 1.15;
  const marginPt = opts.marginPt || 0;

  console.log("[htmlDocsToPdfBlob] start, pages:", htmlDocs.length);
  try {
    const pdf = new jsPDF({ unit: "pt", format: format, orientation: orientation, compress: true });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const availWidth = pageWidth - marginPt * 2;
    const availHeight = pageHeight - marginPt * 2;

    for (let i = 0; i < htmlDocs.length; i++) {
      if (i > 0) pdf.addPage();
      console.log("[htmlDocsToPdfBlob] rendering page", i + 1, "of", htmlDocs.length);
      const canvas = await renderHtmlDocToCanvas(htmlDocs[i], widthPx, scale, "htmlDocsToPdfBlob");
      const imgData = canvas.toDataURL("image/png");
      const imgWidthFull = availWidth;
      const imgHeightFull = (canvas.height * imgWidthFull) / canvas.width;
      /* Always shrink-to-fit onto this single page — one doc, one
         page, no exceptions (business rule). */
      let finalW, finalH;
      if (imgHeightFull <= availHeight * fitToleranceRatio && imgHeightFull <= availHeight) {
        finalW = imgWidthFull;
        finalH = imgHeightFull;
      } else {
        const k = availHeight / imgHeightFull;
        finalW = imgWidthFull * k;
        finalH = availHeight;
      }
      const xOffset = marginPt + (availWidth - finalW) / 2;
      pdf.addImage(imgData, "PNG", xOffset, marginPt, finalW, finalH);
    }
    const blob = pdf.output("blob");
    console.log("[htmlDocsToPdfBlob] jsPDF blob created");
    return blob;
  } catch (e) {
    console.error("[htmlDocsToPdfBlob] error:", e);
    throw e;
  }
}

/* Sanitise a string for use in a file name. Drive accepts most
   characters but Windows / macOS users will eventually share these
   files, so strip the ones that confuse those filesystems. */
export function safeFilename(name) {
  return String(name || "file")
    .replace(/[\\/:*?"<>|]+/g, "_")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .substring(0, 180);
}
